<template>
  <div 
    class="demo-drop dashed-border-ani"
    :class="{ 'drag-over': isDragOver }"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <p class="m font-small tt-up">
      {{ "Demo drop" + (isOwnProfile ? " (Preview)" : "") }}
    </p>

    <div class="buttons d-f g">
      <button class="secondary d-f ai-c g" @click.prevent="handleSubmitDemo">
        <IconBase w="15" h="15" viewBox="0 0 15 15" icon-name="upload">
          <IconMusic />
        </IconBase>
        Submit Demo
      </button>
      <button class="secondary d-f ai-c g" @click.prevent="showAssessmentText">
        <IconBase w="15" h="15" viewBox="0 0 15 15" icon-name="info">
          <IconInfo />
        </IconBase>
        Criteria
      </button>
    </div>

    <p class="m font-small tt-up grey">( mp3, Wav )</p>
    <p v-if="isDragOver" class="m font-small tt-up drag-hint">Drop your file here</p>

    <Modal
      v-if="assessmentModalShowing"
      @hide="assessmentModalShowing = false"
      :width="600"
      :fullscreen-mobile="true"
    >
      <button
        type="button"
        class="small square transparent p-a r-0 t-0 p d-f ai-c jc-c"
        aria-label="Close"
        @click="assessmentModalShowing = false"
      >
        <IconBase w="15" h="15" viewBox="0 0 15 15" icon-name="close">
          <IconCancel />
        </IconBase>
      </button>
      <div class="assessment-modal-content d-f fd-c g p-lg">
        <p class="tt-up as-c mute">Criteria & Assessment</p>
        <p class="b-0 ws-pre-wrap ta-c">
          {{ labelData.demoCriteria ?? "No criteria available" }}
        </p>
      </div>
      <div class="assessment-modal-footer w-100 d-f fd-c ai-c jc-c">
        <button
          class="secondary d-f ai-c jc-c as-c g m-t"
          @click.prevent="handleSubmitDemo"
        >
          <IconBase w="15" h="15" viewBox="0 0 15 15" icon-name="upload">
            <IconMusic />
          </IconBase>
          Submit demo
        </button>
        <p class="m font-small tt-up grey">( mp3, Wav )</p>
      </div>
    </Modal>
  </div>
</template>

<script>
import IconMusic from "@/assets/icons/IconMusic.vue";
import IconInfo from "@/assets/icons/IconInfo.vue";
import IconBase from "@/assets/icons/IconBase.vue";
import IconCancel from "@/assets/icons/IconCancel.vue";
import { mapGetters, mapActions } from "vuex";
import Modal from "@/components/misc/modal.vue";
import { mbToBytes } from "@/common";

export default {
  name: "SubmitDemoCard",
  props: {
    labelUid: {
      type: String,
    },
    labelData: {
      type: Object,
    },
    recipientType: {
      type: String,
      default: "label",
    },
  },
  data() {
    return {
      assessmentModalShowing: false,
      isDragOver: false,
    };
  },
  created() {},
  components: { Modal, IconBase, IconInfo, IconMusic, IconCancel },
  methods: {
    ...mapActions(["setPendingDemoFile"]),
    showAssessmentText() {
      this.assessmentModalShowing = true;
    },
    handleSubmitDemo() {
      if (this.isOwnProfile) {
        alert("Can not submit Demo to self");
        return;
      }

      // Set flag to open file picker when page loads
      this.$store.commit("SET_OPEN_FILE_PICKER", true);
      
      const username = this.recipientType === "artist"
        ? this.labelData.usernameUrl
        : this.labelData.username;
      this.$router.push({
        name: "SubmitDemoPage",
        query: { label: username },
      });
    },
    handleDragOver(event) {
      event.preventDefault();
      if (!this.isOwnProfile) {
        this.isDragOver = true;
      }
    },
    handleDragLeave(event) {
      event.preventDefault();
      // Only remove drag-over state if we're actually leaving the drop zone
      // This prevents flickering when moving over child elements
      const rect = this.$el.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
      
      // Check if we're still within the bounds of the drop zone
      if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
        this.isDragOver = false;
      }
    },
    handleDrop(event) {
      this.isDragOver = false;
      
      if (this.isOwnProfile) {
        alert("Can not submit Demo to self");
        return;
      }

      const files = event.dataTransfer.files;
      if (!files || files.length === 0) {
        return;
      }

      const file = files[0];
      
      // Validate file type - check extension first (more reliable), then MIME type
      const fileName = file.name || '';
      const validExtensions = ['mp3', 'wav'];
      
      // Check if filename contains any valid audio extension
      // This handles cases like .mp3.icloud where .icloud is the last extension
      let isValidExtension = false;
      let foundExtension = null;
      
      for (const ext of validExtensions) {
        // Check if the filename contains the extension (case insensitive)
        const extPattern = new RegExp(`\\.${ext}(\\.|$)`, 'i');
        if (extPattern.test(fileName)) {
          isValidExtension = true;
          foundExtension = ext;
          break;
        }
      }
      
      // Check MIME type as secondary validation (starts with audio/)
      const fileMimeType = file.type || '';
      const isValidMimeType = fileMimeType.startsWith('audio/');
      
      // Accept if either extension or MIME type is valid
      // If both are invalid/empty, reject
      if (!isValidExtension && !isValidMimeType) {
        alert("Invalid file type. Please upload .mp3 or .wav files only.");
        return;
      }

      const maxSizeBytes = mbToBytes(90);
      if (file.size > maxSizeBytes) {
        alert(`File size exceeds 90 MB. Please upload a smaller file.`);
        return;
      }

      // Store the file in Vuex for SubmitDemo to pick up
      this.setPendingDemoFile(file);
      
      // Navigate to SubmitDemoPage
      const username = this.recipientType === "artist"
        ? this.labelData.usernameUrl
        : this.labelData.username;
      this.$router.push({
        name: "SubmitDemoPage",
        query: { label: username },
      });
    },
  },
  computed: {
    ...mapGetters(["getUser"]),

    isOwnProfile() {
      return this.getUser.data?.uid === this.labelUid;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_mixins.scss";
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
.demo-drop {
  padding: 0 20px;
  text-align: center;
  transition: all 0.2s ease;
  position: relative;
  @media #{$mobile} {
    width: 100%;
    box-sizing: border-box;
    
  }
  &.drag-over {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.02);
  }
  
  .drag-hint {
    color: rgba(255, 255, 255, 0.8);
    margin-top: 10px;
    animation: pulse 1.5s ease-in-out infinite;
  }
  .buttons {
    button {
      min-width: 200px;
    }
    @media #{$mobile} {
      flex-direction: column;
    }
  }
}

/* Assessment modal: on mobile, content scrolls and footer stays at bottom */
@media #{$mobile} {
  .assessment-modal-content {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
  }
  .assessment-modal-footer {
    flex: 0 0 auto;
    padding-top: 16px;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}
</style>
